import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, errorText } from '../api';
import type { Bereich, Tarif, TarifGrenze } from '../billtoro/types';
import { fehlendeFunktion, grenzeFehler, planName } from '../billtoro/upgrade';
import { isBilltoro } from '../produkt';

// Billtoro-Tarif im Rechnungswerkzeug: Knöpfe für Funktionen, die der Tarif der Firma nicht enthält, tragen eine kleine
// Marke „Start“ bzw. „Pro“ und öffnen das Fenster „Mehr mit Billtoro“ (billtoro/UpgradeDialog.tsx) statt eines
// Fehlers; Funktionen, die es noch nicht gibt, die Marke „bald“ und keine Aktion. Fehler 402 und „bald“ zeigt das
// Fenster, darum nie noch einmal an Ort und Stelle. In der Kanzlei-App (kein Tarif) nichts davon.

const BILLTORO = isBilltoro();
// Antworten, die api.ts als „nt:tarif“ meldet und das Fenster zeigt (billtoro/UpgradeDialog.tsx, BEKANNT)
const TARIF_CODES = ['tarif_feature', 'tarif_grenze', 'bald', 'post_zahlung'];

export type Fehlt = ReturnType<typeof fehlendeFunktion>;

// Antwort, die das Fenster „Mehr mit Billtoro“ schon zeigt (api.ts meldet 402 und 503 „bald“ als nt:tarif)
export function tarifFehler(err: unknown): boolean {
  return (
    BILLTORO &&
    err instanceof ApiError &&
    (err.status === 402 || (err.status === 503 && err.code === 'bald')) &&
    TARIF_CODES.includes(err.code ?? '')
  );
}

// Fehlertext für die Anzeige an Ort und Stelle, null bei Tarif-Fehlern
export function fehlerText(err: unknown): string | null {
  return tarifFehler(err) ? null : errorText(err);
}

// Marke am Knopf: kleinster Tarif mit der Funktion, oder „bald“
export function TarifMarke({ tarif, fehlt }: { tarif: Tarif | null | undefined; fehlt: Fehlt }) {
  if (!tarif || !fehlt) return null;
  if (fehlt === 'bald') return <span className="badge b-none rt-marke">bald</span>;
  const name = planName(tarif, fehlt);
  return (
    <span className="badge rt-marke rt-marke-plan" title={`Im Tarif ${name}`}>
      {name}
    </span>
  );
}

// Knopf für eine Funktion, die im Tarif fehlt (fehlt nicht null): mit Marke, öffnet das Fenster „Mehr mit Billtoro“
// (onUpgrade: upgradeAnbieten bzw. grenzeAnbieten, vorher ein eigenes offenes Fenster schließen); „bald“: ohne Aktion.
// Ist die Funktion da, steht an der Stelle der gewohnte Knopf oder Link (in der Kanzlei-App immer). role: als Eintrag
// eines Menüs (Rechnungsliste, ZeilenMenue.tsx).
export function GesperrtKnopf({
  tarif,
  fehlt,
  className,
  role,
  onUpgrade,
  children,
}: {
  tarif: Tarif | null | undefined;
  fehlt: Exclude<Fehlt, null>;
  className: string;
  role?: 'menuitem';
  onUpgrade(): void;
  children: ReactNode;
}) {
  return (
    <button type="button" role={role} className={`${className} rt-gesperrt`} disabled={fehlt === 'bald'} onClick={onUpgrade}>
      {children}
      <TarifMarke tarif={tarif} fehlt={fehlt} />
    </button>
  );
}

// Grenze des Tarifs schon erreicht (Rechnungen oder Angebote im Monat, wiederkehrende Rechnungen)? Dann öffnet
// grenzeAnbieten (billtoro/upgrade.ts) das Fenster „Mehr mit Billtoro“ mit derselben Antwort wie der Server.
export function grenzeErreicht(tarif: Tarif | null | undefined, grenze: TarifGrenze): boolean {
  return !!tarif && grenzeFehler(tarif, grenze) !== null;
}

// Marke für eine erreichte Grenze: der Tarif, der mehr erlaubt
export function grenzeFehlt(tarif: Tarif | null | undefined, grenze: TarifGrenze): Fehlt {
  const f = tarif ? grenzeFehler(tarif, grenze) : null;
  return f?.code === 'tarif_grenze' ? (f.details.noetig ?? 'bald') : null;
}

// Schmaler Bildschirm (Handy, bis 720 px wie rechnung.css): z. B. die Leiste unten im Formular, „Teilen“ als Hauptknopf
const SCHMAL = '(max-width: 720px)';
export function useSchmal(): boolean {
  const [schmal, setSchmal] = useState(() => typeof window !== 'undefined' && window.matchMedia(SCHMAL).matches);
  useEffect(() => {
    const m = window.matchMedia(SCHMAL);
    const on = () => setSchmal(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return schmal;
}

// Billtoro: Meine Angaben (Absender, Bankdaten, Steuernummer) ändert nur der Inhaber; ein Team-Mitglied sieht sie nur
// (der Server antwortet sonst 403). In der Kanzlei-App immer änderbar.
export const NUR_INHABER = 'Nur der Inhaber kann das ändern.';
export const nurLesen = (tarif: Tarif | null | undefined): boolean => tarif?.rolle === 'mitglied';

// Billtoro: Rechte je Team-Mitglied (server/src/lib/rechte.ts). Ohne Tarif (Kanzlei-App, Übersicht noch nicht da) alles
// zeigen. Nur Anzeige: ohne Recht fehlen die Knöpfe zum Ändern, die Prüfung macht der Server (403 mit Text).
export const darf = (tarif: Tarif | null | undefined, b: Bereich): boolean => !tarif || tarif.rolle === 'inhaber' || tarif.rechte.includes(b);
// Titel der Bereiche wie auf dem Server (BEREICH, Test billtoro-rechte); Titel und Texte im Rechte-Dialog kommen vom Server
export const BEREICH_TITEL: Record<Bereich, string> = {
  rechnungen: 'Rechnungen',
  angebote: 'Angebote und Aufträge',
  kunden: 'Kontakte, Leistungen und Lager',
  zahlungen: 'Zahlungen',
  ausgaben: 'Ausgaben und Kasse',
  export: 'Exporte',
  einstellungen: 'Einstellungen',
  zeiten: 'Zeiten des Teams',
};
// Einstellungen (Logo, Vorlagen, Nummern, E-Rechnung, E-Mail-Texte, DATEV-Angaben, Mahnläufe) ohne das Recht
export const OHNE_RECHT_EINSTELLUNGEN = 'Ändern dürfen der Inhaber und wer von ihm das Recht „Einstellungen“ hat.';
// Hinweis oben, wenn die Adresse eines Formulars ohne das Recht geöffnet wurde (RechnungTool.tsx OhneRecht)
export const ohneRechtText = (b: Bereich) => `Für das Recht „${BEREICH_TITEL[b]}“ wenden Sie sich bitte an den Inhaber.`;

// Adresse eines Formulars ohne das Recht geöffnet (z. B. /rechnung/neu): zurück zur Liste, dort oben der Hinweis
// (RechnungTool.tsx liest ohneRecht aus dem Zustand der Adresse)
export function OhneRecht({ bereich, zu }: { bereich: Bereich; zu: string }) {
  return <Navigate to={zu} replace state={{ ohneRecht: bereich }} />;
}

// Formular nur mit dem Recht, sonst OhneRecht. Geprüft einmal beim Öffnen: Nimmt der Inhaber das Recht weg, während
// das Formular offen ist (Live, RechnungTool.tsx), bleibt es mit allen Eingaben offen; beim Absenden kommt der 403
// mit Text vom Server.
export function MitRecht({
  tarif,
  bereich,
  zu,
  children,
}: {
  tarif: Tarif | null | undefined;
  bereich: Bereich;
  zu: string;
  children: ReactNode;
}) {
  const [ok] = useState(() => darf(tarif, bereich));
  return ok ? <>{children}</> : <OhneRecht bereich={bereich} zu={zu} />;
}

// Abzeichen „Nur ansehen“ im Kopf einer Seite, wenn das Recht des Bereichs fehlt
export function NurAnsehen({ tarif, bereich }: { tarif: Tarif | null | undefined; bereich: Bereich }) {
  if (darf(tarif, bereich)) return null;
  const text = `Ändern darf, wem der Inhaber das Recht „${BEREICH_TITEL[bereich]}“ gibt.`;
  return (
    <span className="badge b-none rt-nuransehen" title={text}>
      Nur ansehen<span className="sr-only">. {text}</span>
    </span>
  );
}

// Felder nur zum Ansehen (ohne nurLesen genau der Inhalt, ohne Hülle); ohneHinweis: der Hinweis steht schon weiter oben
// in derselben Karte; hinweis: warum (Standard: nur der Inhaber)
export function NurLesen({
  an,
  ohneHinweis = false,
  hinweis = NUR_INHABER,
  children,
}: {
  an: boolean;
  ohneHinweis?: boolean;
  hinweis?: string;
  children: ReactNode;
}) {
  if (!an) return <>{children}</>;
  return (
    <fieldset disabled className="rt-nurlesen">
      {!ohneHinweis && <p className="rt-hint rt-nurlesen-h">{hinweis}</p>}
      {children}
    </fieldset>
  );
}
